import React, { forwardRef, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import bowser from 'yola-bowser';
import { designSystem } from '@yola/ws-ui';

const { Heading, AutocompleteField } = designSystem;

const defaultRules = {};

const AutocompleteControl = forwardRef(
  (
    {
      title,
      short = false,
      control,
      name,
      rules = defaultRules,
      options,
      modalContentRef,
      contentRef,
      isAdvancedFontCustomizationAvailable,
      inputValue,
      onChange,
      onInputChange,
      onKeyDown,
      onFocus,
      ...otherProps
    },
    ref
  ) => {
    const selectWrapperRef = useRef(null);
    const selectRef = useRef(null);
    const [menuPlacement, setMenuPlacement] = useState('auto');
    const isMobile = bowser.mobile;

    const classes = classNames('ws-design-fonts-dialog__select', {
      'ws-design-fonts-dialog__select--short': short,
    });

    const handleFocus = () => {
      onFocus();

      if (isMobile) {
        selectRef?.current?.openMenuAutocomplete();
      }
    };

    useEffect(() => {
      const contentContainer = contentRef.current;

      const handleClick = () => {
        const modalBottom = modalContentRef.current?.getBoundingClientRect().bottom;
        const selectBottom = selectWrapperRef.current?.getBoundingClientRect().bottom;

        if (modalBottom - selectBottom < 48) {
          setMenuPlacement('top');
        } else {
          setMenuPlacement('auto');
        }
      };

      contentContainer?.addEventListener('scroll', handleClick);

      return () => contentContainer?.removeEventListener('scroll', handleClick);

      // eslint-disable-next-line yola/react-hooks/exhaustive-deps
    }, []);

    return (
      <div className="ws-design-fonts-dialog__control">
        <div className="ws-design-fonts-dialog__control--title">
          <Heading type="heading-6" singleLine>
            {title}
          </Heading>
        </div>
        <div className={classes} ref={selectWrapperRef}>
          <AutocompleteField
            ref={ref}
            control={control}
            name={name}
            rules={rules}
            options={options}
            onChange={onChange}
            placeholder="..."
            inputValue={inputValue}
            onInputChange={onInputChange}
            filterOptionsFunction={(value) => value}
            shouldMarkSelectedOption
            withDropdownIndicator
            blurInputOnSelect
            preventOverflow
            {...(isAdvancedFontCustomizationAvailable && { openMenuOnFocus: true })}
            clearable={false}
            menuPlacement={menuPlacement}
            onKeyDown={onKeyDown}
            onFocus={handleFocus}
            {...otherProps}
          />
        </div>
      </div>
    );
  }
);

AutocompleteControl.displayName = 'AutocompleteControl';

AutocompleteControl.propTypes = {
  title: PropTypes.node.isRequired,
  // eslint-disable-next-line yola/react/require-default-props
  short: PropTypes.bool,
  control: PropTypes.shape({}).isRequired,
  name: PropTypes.string.isRequired,
  // eslint-disable-next-line yola/react/require-default-props
  rules: PropTypes.shape({}),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      label: PropTypes.string,
    })
  ).isRequired,
  modalContentRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
  contentRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }).isRequired,
  isAdvancedFontCustomizationAvailable: PropTypes.bool.isRequired,
  inputValue: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
};

export default AutocompleteControl;
